// Submissions — the entries collected against one or more records. POST
// /submissions/accessed-submissions takes
// `{ type, recordIds, submissionStatus, sourceFilter?, flowState?, companyStructureNodes?,
// filledBy?, dateRange?, fromDate?, toDate?, search?, orderBy?, page, limit }`,
// where `type` says which KIND of record the `recordIds` point at; Reports asks
// for a single form's submissions, while the Inspections → Submissions tab sends
// an EMPTY `recordIds` — which the backend reads as EVERY accessible record of
// `type`, not as a missing value.
//
// `type` and `sourceFilter` read alike and do different jobs: `type` picks
// WHICH records to list submissions for, while `sourceFilter` narrows the
// results to those captured against an inspection or a task WITHIN them. The
// filter has no "all" member — all is the absence of the param, so it is
// omitted from the body rather than sent empty.
//
// The body's `type` and the response's `inspection` key are unrelated: the
// response is not a discriminated union keyed off the request, so `inspection`
// is modelled as one optional populated record and nothing derives its presence
// from `type`. When it's absent the NAME and ID & REF NO. cells dash.
//
// `orderBy` covers three columns only — reference, date and status. The other
// columns are declared without `sortable` so no header draws a chevron the
// backend cannot honour.
//
// `sr` is the row's serial number for the design's `#` column, stamped here from
// the requested page/limit so it keeps counting across pages — the response has
// no such field, and the table primitive stays index-free.
//
// `submissionFlowState` is a confirmed three-value enum, so it is typed as one
// and the status map that keys off it is exhaustive.
//
// GET /submissions/report/:id/:reportType/:language is the second endpoint here
// and is unlike every other one in the app: it returns a finished,
// server-rendered HTML document rather than data, so there is no domain type to
// map it onto — the query's success type is just `string`.
//
// `:language` is the language the SERVER renders the document in, not the admin
// panel's locale — the two are independent axes, and switching the app language
// never changes it. ReportViewPage sends `en` by default and flips it from the
// report's own language toggle.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { ApiErrorShape } from '../types';
import type { FormDateFilter, FormPerson } from './formsApi';
import type { WorkflowElementActionType } from './workflowsApi';
import type { AppLanguage } from '@/redux/language/languageSlice';

/** Which records' submissions to list — the body's `type`, naming the kind of
 *  record `recordIds` point at. */
export enum SubmissionSourceType {
  Inspection = 'inspection',
  Task = 'task',
  Form = 'form',
}

/** Narrows a listing to the submissions captured against one kind of source —
 *  the toolbar's Inspections / Tasks pills. There is deliberately no `All`
 *  member: all is the absence of the param, so the pills' third state omits
 *  `sourceFilter` from the body entirely. */
export enum SubmissionSourceFilter {
  Inspection = 'inspection',
  Task = 'task',
}

/** Backend `orderBy.key` values. Only these three columns are sortable. */
export enum SubmissionSortKey {
  ReferenceNumber = 'referenceNumber',
  CompleteDate = 'completeDate',
  FlowState = 'submissionFlowState',
}

/** Which render of the report to fetch — the `:reportType` path segment.
 *  `Web` is the on-screen report; `Pdf` is the print/document variant. */
export enum SubmissionReportType {
  Web = 'web',
  Pdf = 'pdf',
}

/** Which list the body's `submissionStatus` asks for — the toolbar's Archived
 *  toggle. Always sent: a listing is in exactly one of the two states. */
export enum SubmissionListStatus {
  Active = 'active',
  Archived = 'archived',
}

/** Where a submission sits in its approval workflow (`submissionFlowState`). */
export enum SubmissionFlowState {
  /** Finished — "Completed". */
  End = 'end',
  /** Sent back to the submitter — "Returned". */
  User = 'user',
  /** Sitting on a workflow step — "Awaiting review". */
  Step = 'step',
}

export interface SubmissionQueryArgs {
  type: SubmissionSourceType;
  /** The owning records' ids (one form id when type is Form). An EMPTY array is
   *  a legitimate request meaning EVERY accessible record of `type` — how the
   *  Inspections → Submissions tab lists all inspections' submissions without an
   *  id-gathering round-trip first. So an empty array must never be read as
   *  "nothing to ask for": don't derive a query `skip` from its length. */
  recordIds: string[];
  /** The active rows or the archived list — see SubmissionListStatus. */
  submissionStatus: SubmissionListStatus;
  /** Omit for all sources — see SubmissionSourceFilter. */
  sourceFilter?: SubmissionSourceFilter;
  /** Toolbar-filter params — each omitted when its category has no selection. */
  flowState?: SubmissionFlowState[];
  companyStructureNodes?: string[];
  filledBy?: string[];
  /** The same five-value date vocabulary as the forms listing — imported, not
   *  redeclared, so the shared date-preset helpers type through unchanged. */
  dateRange?: FormDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  fromDate?: string;
  toDate?: string;
  search?: string;
  orderBy?: { key: SubmissionSortKey; type: 'asc' | 'desc' };
  page: number;
  limit: number;
}

/** The record a submission was captured against — the response's `inspection`. */
export interface SubmissionSource {
  id: string;
  referenceNumber: number;
  title: string;
}

/** The structure node the submission was filed under: its own name, its level's
 *  name + glyph, and its parent's name — the three lines the COMPANY STRUCTURE
 *  cell stacks. `parentName` is '' for a root node (no parent to name). */
export interface SubmissionNode {
  id: string;
  name: string;
  levelName: string;
  levelIcon: string;
  parentName: string;
}

export interface Submission {
  id: string;
  /** Serial number across the whole list (not per page) — the `#` column. */
  sr: number;
  referenceNumber: number;
  /** ISO — the response's `completeDate`, carried through unrenamed. */
  completeDate: string;
  submissionFlowState: SubmissionFlowState;
  submittedBy: FormPerson | null;
  source: SubmissionSource | null;
  node: SubmissionNode | null;
  comments: number;
}

export interface SubmissionPage {
  items: Submission[];
  total: number;
}

/** One report section — `referenceId` matches a `data-referenceid` attribute
 *  on the section's element inside the rendered `html` (the document carries
 *  no ids), so "jump to" is an attribute-selector lookup + scroll. */
export interface SubmissionReportSection {
  name: string;
  referenceId: string;
}

export interface SubmissionReport {
  /** The whole rendered document, mounted verbatim. */
  html: string;
  totalPages: number;
  sections: SubmissionReportSection[];
}

export interface SubmissionReportArgs {
  /** The submission's own id. */
  id: string;
  reportType: SubmissionReportType;
  /** The language the server renders the document in — reuses the app's
   *  `AppLanguage` union for the values, not for its value. */
  language: AppLanguage;
}

export interface SubmissionPdfUrlArgs {
  /** The submission's own id. */
  id: string;
  /** The language the PDF is rendered in — the same axis as the report's. */
  language: AppLanguage;
}

/** The target step of a `next_step` element. The populated shape is NOT yet
 *  confirmed (every sample so far carries `nextStep: null`) — extend it against
 *  a real next_step response before rendering more than what's here. */
export interface SubmissionWorkflowNextStep {
  id?: string;
  title?: string;
  position?: number;
}

/** One response the reviewer can pick on the submission's current step — the
 *  Workflow actions drawer's cards. `actionType` reuses the builder's routing
 *  vocabulary (one enum for the definition and the live step). */
export interface SubmissionWorkflowElement {
  id: string;
  position: number;
  /** The response's label — "Approve", "Reject", … */
  title: string;
  actionType: WorkflowElementActionType;
  /** Populated for `next_step` only; null on end_workflow / back_to_user. */
  nextStep: SubmissionWorkflowNextStep | null;
}

/** GET /submissions/workflow-step-details/:id — the submission's CURRENT step. */
export interface SubmissionWorkflowStep {
  id: string;
  /** The step's own name — the drawer's meta line ("Manager"). */
  title: string;
  position: number;
  totalSteps: number;
  elements: SubmissionWorkflowElement[];
}

/** The flow state carried by a structured submissions rejection —
 *  `{success: false, message, error: {flowState}}`: `end` means the workflow is
 *  complete, `user` means the submission is back with the filler. A FALSY
 *  result (null or not sent — the backend deliberately sends no meaningful
 *  value here) means the reviewer is not allowed to perform the action. The
 *  base query passes the envelope's error object through as `data`; this is
 *  the typed narrowing beside the contract that owns it. */
export const workflowFlowStateOf = (e?: ApiErrorShape): SubmissionFlowState | undefined =>
  (e?.data as { flowState?: SubmissionFlowState } | undefined)?.flowState;

/** One entry of GET /submission-history/details/:submissionId — an OLDER PDF
 *  version this submission has read on. The CURRENT version is NOT in the
 *  list — it is implicit, numbered `history.length + 1` (an empty history
 *  means the current version is the only one). Entries are newest first;
 *  `version` is stamped in the transform, `length - index`. `linkedType` is
 *  the source record's kind — the ONLY payload so far that distinguishes
 *  inspection from task, so the modal's ID & REF NO. can prefix correctly
 *  where the listing cannot. */
export interface SubmissionPdfVersion {
  /** The history entry's own id — what the download endpoint is keyed by. */
  id: string;
  formTitle: string;
  submitterName: string;
  submissionReferenceNumber: number;
  linkedType: SubmissionSourceFilter;
  linkedReferenceNumber: number;
  /** 1-based version number; the highest older one is `count`, the implicit
   *  current version is `count + 1`. */
  version: number;
}

/** PATCH /submissions/update-step/:submissionId — fire one response element.
 *  `submissionId` rides in the path; the body is `{stepId, elementId}`. */
export interface UpdateSubmissionStepArgs {
  submissionId: string;
  stepId: string;
  elementId: string;
}

/** POST /submissions/bulk-action `action` values (confirmed four-member enum).
 *  The More menu sends three: Archive → `archive`, Restore → `active`,
 *  Delete → `trash` (a soft delete); `delete` is the permanent one, for a
 *  later trash surface. */
export enum SubmissionBulkAction {
  Active = 'active',
  Archive = 'archive',
  Trash = 'trash',
  Delete = 'delete',
}

export interface BulkSubmissionArgs {
  submissionIds: string[];
  action: SubmissionBulkAction;
}

// ---- raw shapes (per the confirmed response) ----
interface ApiSubmissionPerson {
  _id: string;
  user: { _id: string; firstName: string; lastName: string };
}
interface ApiSubmissionSource {
  _id: string;
  referenceNumber: number;
  title: string;
}
interface ApiSubmissionNode {
  _id: string;
  name: string;
  level: { name: string; icon: string };
  /** Absent/null on a root node. Only `name` is modelled — it's all the UI shows. */
  parent?: { name: string } | null;
}
interface ApiSubmission {
  _id: string;
  referenceNumber: number;
  submissionFlowState: SubmissionFlowState;
  completeDate: string;
  comments: number;
  user?: ApiSubmissionPerson | null;
  inspection?: ApiSubmissionSource | null;
  companyStructureNode?: ApiSubmissionNode | null;
}
interface ApiSubmissionPage {
  submissions: ApiSubmission[];
  totalCount: number;
}
/** The report response's inner payload — the rendered document plus its
 *  outline (per the confirmed sample): `totalPages` for the PDF overlay's
 *  page count, `reportData` for the Jump-to sections. */
interface ApiSubmissionReport {
  html: string;
  totalPages: number;
  reportData: SubmissionReportSection[];
}
/** The download response's inner payload — a presigned link, not the file. */
interface ApiSubmissionPdfUrl {
  url: string;
}
/** One raw history entry — note `id`, not the usual `_id` (per the confirmed
 *  sample), so nothing here renames. */
interface ApiSubmissionHistoryEntry {
  id: string;
  formTitle: string;
  submitterName: string;
  submissionReferenceNumber: number;
  linkedType: SubmissionSourceFilter;
  linkedReferenceNumber: number;
}
interface ApiSubmissionHistory {
  history: ApiSubmissionHistoryEntry[];
}
/** Raw workflow-step payload. `nextStep`'s populated shape is unconfirmed —
 *  see SubmissionWorkflowNextStep. */
interface ApiWorkflowStepElement {
  _id: string;
  position: number;
  title: string;
  actionType: WorkflowElementActionType;
  nextStep: { _id?: string; title?: string; position?: number } | null;
}
interface ApiWorkflowStep {
  _id: string;
  title: string;
  position: number;
  totalSteps: number;
  elements: ApiWorkflowStepElement[];
}

// Same nested `{ _id, user: { firstName, lastName } }` shape as the forms
// payload's people — mirrors formsApi's mapper rather than a second person type.
const toPerson = (p?: ApiSubmissionPerson | null): FormPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toSource = (s?: ApiSubmissionSource | null): SubmissionSource | null =>
  s ? { id: s._id, referenceNumber: s.referenceNumber, title: s.title } : null;

const toNode = (n?: ApiSubmissionNode | null): SubmissionNode | null =>
  n
    ? {
        id: n._id,
        name: n.name,
        levelName: n.level.name,
        levelIcon: n.level.icon,
        parentName: n.parent?.name ?? '',
      }
    : null;

const toSubmission = (s: ApiSubmission, sr: number): Submission => ({
  id: s._id,
  sr,
  referenceNumber: s.referenceNumber,
  completeDate: s.completeDate,
  submissionFlowState: s.submissionFlowState,
  submittedBy: toPerson(s.user),
  source: toSource(s.inspection),
  node: toNode(s.companyStructureNode),
  comments: s.comments,
});

export const submissionsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAccessedSubmissions: build.query<SubmissionPage, SubmissionQueryArgs>({
      query: (body) => ({ ...ROUTES.submissions.accessed, data: body }),
      // The third transform argument is the query arg, so the serial number is
      // stamped from the page actually requested.
      transformResponse: (res: ApiSubmissionPage, _meta, arg: SubmissionQueryArgs) => ({
        items: res.submissions.map((s, i) => toSubmission(s, (arg.page - 1) * arg.limit + i + 1)),
        total: res.totalCount,
      }),
      providesTags: [{ type: 'Submission', id: 'LIST' }],
    }),

    // The report document is server-rendered HTML — nothing here parses or
    // reshapes it. Alongside it ride `totalPages` (the PDF overlay's page
    // count) and the section outline, renamed `reportData → sections` (the
    // `totalCount → total` idiom). A query (not the throwaway spike's
    // mutation) so it caches per submission and the page gets loading/error
    // states for free.
    getSubmissionReport: build.query<SubmissionReport, SubmissionReportArgs>({
      query: ({ id, reportType, language }) => ({
        ...ROUTES.submissions.report(id, reportType, language),
      }),
      // axiosBaseQuery already unwraps one `{ success, data }` layer; the
      // payload sits one level below that.
      transformResponse: (res: ApiSubmissionReport): SubmissionReport => ({
        html: res.html,
        totalPages: res.totalPages,
        sections: res.reportData,
      }),
      // All four renders (web/pdf × en/ar) share one tag on purpose — they are
      // four views of one submission, so anything invalidating it drops all four.
      // Caching is unaffected: RTK Query keys the cache on the serialized arg, so
      // each pair is fetched once and a switch back is served from cache.
      providesTags: (_result, _error, arg) => [{ type: 'Submission', id: arg.id }],
    }),

    // A GET that mints a one-shot artefact: the finished PDF is already built
    // server-side and this hands back a presigned link to it, so the app never
    // sees the file's bytes through the api layer.
    //
    // Modelled as a query with a LAZY hook rather than a mutation — as
    // getSubmissionReport was, deliberately — so a click triggers it instead of a
    // render. The lazy trigger defaults to forceRefetch, and keepUnusedDataFor: 0
    // drops the entry as soon as the page unsubscribes, so every click mints a
    // freshly signed link and an expired one is never replayed from cache. No
    // providesTags: this is an action's result, not cached state.
    getSubmissionPdfUrl: build.query<string, SubmissionPdfUrlArgs>({
      query: ({ id, language }) => ({ ...ROUTES.submissions.downloadPdf(id, language) }),
      transformResponse: (res: ApiSubmissionPdfUrl) => res.url,
      keepUnusedDataFor: 0,
    }),

    // The submission's OLDER PDF versions — what the PDF versions modal lists
    // under its implicit current-version row. Version numbers are stamped here
    // (newest first, so entry i of n is n - i), the `sr` idiom: the response
    // carries no such field and the modal stays index-free. Shares the
    // per-submission tag so a workflow transition or lifecycle move drops it
    // with the renders.
    getSubmissionPdfHistory: build.query<SubmissionPdfVersion[], string>({
      query: (submissionId) => ({ ...ROUTES.submissions.pdfHistory(submissionId) }),
      transformResponse: (res: ApiSubmissionHistory) =>
        res.history.map((e, i) => ({ ...e, version: res.history.length - i })),
      providesTags: (_result, _error, submissionId) => [{ type: 'Submission', id: submissionId }],
    }),

    // A historical version's presigned link — getSubmissionPdfUrl verbatim but
    // keyed by the HISTORY entry's id: lazy so a click (not a render) triggers
    // it, keepUnusedDataFor: 0 so every click mints a fresh link and an expired
    // one is never replayed from cache. No providesTags: an action's result,
    // not cached state.
    getSubmissionHistoryPdfUrl: build.query<string, string>({
      query: (historyId) => ({ ...ROUTES.submissions.pdfHistoryUrl(historyId) }),
      transformResponse: (res: ApiSubmissionPdfUrl) => res.url,
      keepUnusedDataFor: 0,
    }),

    // The submission's current workflow step — what the Workflow actions drawer
    // lists. Shares the per-submission tag with the report renders on purpose:
    // a future transition invalidates the step and the documents together.
    getSubmissionWorkflowStep: build.query<SubmissionWorkflowStep, string>({
      query: (id) => ({ ...ROUTES.submissions.workflowStepDetails(id) }),
      transformResponse: (res: ApiWorkflowStep) => ({
        id: res._id,
        title: res.title,
        position: res.position,
        totalSteps: res.totalSteps,
        elements: [...res.elements]
          .sort((a, b) => a.position - b.position)
          .map((e) => ({
            id: e._id,
            position: e.position,
            title: e.title,
            actionType: e.actionType,
            nextStep: e.nextStep
              ? { id: e.nextStep._id, title: e.nextStep.title, position: e.nextStep.position }
              : null,
          })),
      }),
      providesTags: (_result, _error, id) => [{ type: 'Submission', id }],
    }),

    // The workflow transition. Invalidating the per-submission tag is the point
    // of sharing it: one write refetches the step details AND both report
    // renders; the LIST tag refreshes the submissions table's STATUS chip.
    updateSubmissionStep: build.mutation<unknown, UpdateSubmissionStepArgs>({
      query: ({ submissionId, stepId, elementId }) => ({
        ...ROUTES.submissions.updateStep(submissionId),
        data: { stepId, elementId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Submission', id: arg.submissionId },
        { type: 'Submission', id: 'LIST' },
      ],
    }),

    // Kicks off a server-side CSV export of the selected submissions — a job,
    // not a download: nothing comes back to consume, so success just means
    // "started". No invalidatesTags on purpose: an export reads rows, it
    // changes nothing, so no cache should refetch over it.
    exportSubmissionsCsv: build.mutation<unknown, string[]>({
      query: (submissionIds) => ({ ...ROUTES.submissions.exportCsv, data: { submissionIds } }),
    }),

    // One submission's attachments, same export-manager job family — the id
    // rides in the path, no body, nothing comes back to consume, and nothing
    // is invalidated (exports change no rows).
    exportSubmissionAttachments: build.mutation<unknown, string>({
      query: (submissionId) => ({ ...ROUTES.submissions.exportAttachments(submissionId) }),
    }),

    // Lifecycle moves. Bulk-capable, so the selection bar's one call covers any
    // selection size; each affected id is invalidated alongside the list so a
    // restored submission's cached renders don't outlive the move.
    bulkSubmissionAction: build.mutation<unknown, BulkSubmissionArgs>({
      query: (body) => ({ ...ROUTES.submissions.bulkAction, data: body }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Submission', id: 'LIST' },
        ...arg.submissionIds.map((id) => ({ type: 'Submission' as const, id })),
      ],
    }),
  }),
});

export const {
  useGetAccessedSubmissionsQuery,
  useGetSubmissionReportQuery,
  useLazyGetSubmissionPdfUrlQuery,
  useGetSubmissionPdfHistoryQuery,
  useLazyGetSubmissionHistoryPdfUrlQuery,
  useGetSubmissionWorkflowStepQuery,
  useUpdateSubmissionStepMutation,
  useBulkSubmissionActionMutation,
  useExportSubmissionsCsvMutation,
  useExportSubmissionAttachmentsMutation,
} = submissionsApi;
